import React, { useEffect, useRef, useState } from 'react';

import { VideoView, useVideoPlayer } from 'expo-video';
import { useIntl } from 'react-intl';
import { View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAppTakeoverCart } from 'components/app-takeover/use-app-takeover-cart';
import Modal from 'components/modal';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { Deferred } from 'utils/Deferred';
import { isWeb } from 'utils/environment';

import { BottomLayer, Card, Message, ModalContainer, TopLayer } from './styled';

export const PaymentProcessLoading = ({
  commitOrderSuccess,
  navigateToOrderConfirmation,
}: {
  commitOrderSuccess: boolean;
  navigateToOrderConfirmation: () => void;
}) => {
  const {
    paymentProcessingVideo: takeoverCheckoutVideo,
    paymentProcessingBackgroundColor: takeoverBgColor,
  } = useAppTakeoverCart();
  const animationCompleteDeferred = useRef(new Deferred());
  const { top: topSafeArea } = useSafeAreaInsets();

  const player = useVideoPlayer(takeoverCheckoutVideo, player => {
    player.loop = true;

    if (isWeb) {
      // web requires a timeout due to a bug, maybe could remove in SDK 54 https://github.com/expo/expo/issues/36350
      setTimeout(() => player.play(), 0);
    } else {
      player.play();
    }

    player.addListener('playToEnd', () => animationCompleteDeferred.current.resolve());
    player.addListener('statusChange', status => {
      if (status.error) {
        animationCompleteDeferred.current.resolve();
      }
    });
    // ensure promise resolves if all else fails (with a 1s extra buffer)
    const durationSec = Number.isFinite(player.duration) ? player.duration : 5; // fallback to 5s if undefined
    setTimeout(() => animationCompleteDeferred.current.resolve(), (durationSec + 1) * 1000);
  });

  const isDesktop = useIsDesktopBreakpoint();
  const { height, width } = useWindowDimensions();
  const { formatMessage } = useIntl();
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (commitOrderSuccess) {
      if (isHidden) {
        navigateToOrderConfirmation?.();
        return;
      }
      animationCompleteDeferred.current.promise.then(() => {
        navigateToOrderConfirmation?.();
      });
    }
  }, [commitOrderSuccess, isHidden, navigateToOrderConfirmation]);

  if (isHidden) {
    return null;
  }

  return (
    <Modal
      hideStatusBar={true}
      amplitudeEventData={{ modalAppearanceEventMessage: 'Processing Order Modal' }}
      contentStyle={{ height: '100%' }}
      scrollViewBounces={false}
      onDismiss={() => {
        player?.pause();
        setIsHidden(true);
      }}
      isFullHeightContent
    >
      <ModalContainer height={height}>
        <BottomLayer width={width} height={height}>
          <View style={{ width, height, backgroundColor: takeoverBgColor }}>
            <VideoView style={{ height }} player={player} />
          </View>
          {isDesktop && <Message>{formatMessage({ id: 'cartCardLoadingMessage' })}</Message>}
        </BottomLayer>
        {!isDesktop && (
          <TopLayer top={topSafeArea}>
            <Card>
              <Message>{formatMessage({ id: 'cartCardLoadingMessage' })}</Message>
            </Card>
          </TopLayer>
        )}
      </ModalContainer>
    </Modal>
  );
};
